import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { getMetrics, getMetricsBatch, getMetricsTagFacets } from '@/features/metrics/data';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { queryKeys, useIsAuthenticated } from '@/shared/hooks';
import { metricsApiToDto } from '../metricsAdapters';
import type { ExecutionStatusKey, MetricsDto, MetricsRange, MetricsScope } from '../metricsHelpers';
import type { ApiError } from '@/shared/data/client';

type MetricsQueryInput = {
  range: MetricsRange;
  statuses: ExecutionStatusKey[];
  tags?: string[];
  scope: MetricsScope;
  projectId?: string | null;
  entityId?: string | null;
  name?: string | null;
  day?: string | null;
  startDay?: string | null;
  endDay?: string | null;
  latestOnly?: boolean;
  refreshKey?: string | number | null;
  enabled?: boolean;
  refetchOnWindowFocus?: boolean;
  staleTimeMs?: number;
};

type MetricsBatchQueryInput = {
  range: MetricsRange;
  statuses?: ExecutionStatusKey[];
  tags?: string[];
  scope: MetricsScope;
  name?: string | null;
  startDay?: string | null;
  endDay?: string | null;
  latestOnly?: boolean;
  entities: Array<{ entityId: string; projectId?: string | null }>;
  enabled?: boolean;
  staleTimeMs?: number;
};

type MetricsBatchResultItem = {
  entityId: string;
  projectId: string | null;
  metrics: MetricsDto;
};

type MetricsTagFacetCountsInput = {
  range: MetricsRange;
  statuses?: ExecutionStatusKey[];
  scope: MetricsScope;
  projectId?: string | null;
  entityId?: string | null;
  name?: string | null;
  startDay?: string | null;
  endDay?: string | null;
  latestOnly?: boolean;
  tags: string[];
  enabled?: boolean;
  staleTimeMs?: number;
};

const buildStatusKey = (statuses: ExecutionStatusKey[]): string => {
  if (statuses.length === 0) return 'all';
  return [...statuses].sort().join('|');
};

const buildTagsKey = (tags: string[] | undefined): string => {
  if (!tags || tags.length === 0) return 'all';
  return [...tags].sort().join('|');
};

const METRICS_BATCH_ENTITY_LIMIT = 100;

const chunkMetricsEntities = (
  entities: MetricsBatchQueryInput['entities'],
): Array<MetricsBatchQueryInput['entities']> => {
  const chunks: Array<MetricsBatchQueryInput['entities']> = [];
  for (let index = 0; index < entities.length; index += METRICS_BATCH_ENTITY_LIMIT) {
    chunks.push(entities.slice(index, index + METRICS_BATCH_ENTITY_LIMIT));
  }
  return chunks;
};

export const useMetricsQuery = ({
  range,
  statuses,
  tags,
  scope,
  projectId,
  entityId,
  name,
  day,
  startDay,
  endDay,
  latestOnly = false,
  refreshKey,
  enabled = true,
  refetchOnWindowFocus = false,
  staleTimeMs,
}: MetricsQueryInput): UseQueryResult<MetricsDto, ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  const statusKey = buildStatusKey(statuses);
  const tagsKey = buildTagsKey(tags);
  const nameKey = name?.trim().toLowerCase() ?? 'all';
  const entityKey = entityId ?? 'all';
  const dayKey = day ?? 'all';
  const rangeKey = startDay && endDay ? `${startDay}_${endDay}` : 'all';
  const latestKey = latestOnly ? 'latest' : 'all';
  return useQuery<MetricsDto, ApiError>({
    queryKey: queryKeys.metrics(
      orgKey,
      projectId ?? null,
      scope,
      range,
      statusKey,
      nameKey,
      entityKey,
      dayKey,
      rangeKey,
      latestKey,
      refreshKey ?? null,
      tagsKey,
    ),
    enabled: isAuthenticated && enabled,
    queryFn: async (): Promise<MetricsDto> => {
      const apiData = await getMetrics(
        {
          range,
          statuses,
          tags,
          scope,
          projectId,
          entityId,
          name,
          day,
          startDay,
          endDay,
          latestOnly,
        },
        orgForQuery,
      );
      return metricsApiToDto(apiData);
    },
    staleTime: staleTimeMs ?? 2 * 60_000,
    refetchOnWindowFocus,
    refetchOnReconnect: false,
  });
};

export const useMetricsBatchQuery = ({
  range,
  statuses = [],
  tags,
  scope,
  name,
  startDay,
  endDay,
  latestOnly = false,
  entities,
  enabled = true,
  staleTimeMs,
}: MetricsBatchQueryInput): UseQueryResult<MetricsBatchResultItem[], ApiError> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const orgForQuery = selectedOrganisationId ?? undefined;
  const statusKey = buildStatusKey(statuses);
  const tagsKey = buildTagsKey(tags);
  const nameKey = name?.trim().toLowerCase() ?? 'all';
  const rangeKey = startDay && endDay ? `${startDay}_${endDay}` : 'all';
  const latestKey = latestOnly ? 'latest' : 'all';
  const entityKey = entities
    .map((entity) => `${entity.projectId ?? 'all'}:${entity.entityId}`)
    .sort()
    .join('|');

  return useQuery<MetricsBatchResultItem[], ApiError>({
    queryKey: queryKeys.metricsBatch(
      orgKey,
      scope,
      range,
      entityKey,
      rangeKey,
      statusKey,
      tagsKey,
      `${latestKey}:${nameKey}`,
    ),
    enabled: isAuthenticated && enabled && entities.length > 0,
    queryFn: async (): Promise<MetricsBatchResultItem[]> => {
      const chunks = chunkMetricsEntities(entities);
      const apiData = (
        await Promise.all(
          chunks.map(
            async (chunk) =>
              await getMetricsBatch(
                {
                  range,
                  statuses,
                  tags,
                  scope,
                  name,
                  startDay,
                  endDay,
                  latestOnly,
                  entities: chunk,
                },
                orgForQuery,
              ),
          ),
        )
      ).flat();
      return apiData.map((item) => ({
        entityId: item.entityId,
        projectId: item.projectId,
        metrics: metricsApiToDto(item.metrics),
      }));
    },
    staleTime: staleTimeMs ?? 2 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useMetricsTagFacetCounts = ({
  range,
  statuses = [],
  scope,
  projectId,
  entityId,
  name,
  startDay,
  endDay,
  latestOnly = false,
  tags,
  enabled = true,
  staleTimeMs,
}: MetricsTagFacetCountsInput): Partial<Record<string, number>> => {
  const isAuthenticated = useIsAuthenticated();
  const { selectedOrganisationId } = useOrganisationFilter();
  const orgKey = selectedOrganisationId ?? null;
  const statusKey = buildStatusKey(statuses);
  const nameKey = name?.trim().toLowerCase() ?? 'all';
  const entityKey = entityId ?? 'all';
  const rangeKey = startDay && endDay ? `${startDay}_${endDay}` : 'all';
  const latestKey = latestOnly ? 'latest' : 'all';
  const uniqueTags = [...new Set(tags)].sort((left, right) => left.localeCompare(right));
  const tagsKey = buildTagsKey(uniqueTags);

  const query = useQuery({
    queryKey: queryKeys.metricsTagFacets(
      orgKey,
      projectId ?? null,
      scope,
      range,
      tagsKey,
      rangeKey,
      statusKey,
      nameKey,
      entityKey,
      latestKey,
    ),
    enabled: isAuthenticated && enabled && uniqueTags.length > 0,
    staleTime: staleTimeMs ?? 2 * 60_000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    queryFn: async (): Promise<Partial<Record<string, number>>> => {
      const counts = await getMetricsTagFacets(
        {
          range,
          statuses,
          scope,
          projectId,
          entityId,
          name,
          startDay,
          endDay,
          latestOnly,
          tags: uniqueTags,
        },
        orgKey,
      );
      return Object.fromEntries(counts.map((item) => [item.tag, item.count]));
    },
  });

  return query.data ?? {};
};
